'use client';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils.ts';

/** Button variant types representing different visual styles */
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'accent';

/** Button size types for different button dimensions */
type ButtonSize = 'l' | 'm' | 's' | 'icon-l' | 'icon-m' | 'icon-s' | 'icon-xs' | 'icon-tiny';

/**
 * Button variant configuration using class-variance-authority
 * Defines the styling for different button variants and sizes
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-full whitespace-nowrap outline-none focus-visible:border-ring focus-visible:ring-brand focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive disabled:pointer-events-none disabled:opacity-32',
  {
    variants: {
      variant: {
        primary: 'text-foreground-inverse bg-button-background hover:bg-button-background-hover',
        secondary: 'text-foreground bg-button-background-inverse hover:bg-button-background-inverse-hover',
        ghost: 'text-foreground bg-alpha-80 hover:bg-alpha-50',
        accent: 'text-foreground dark:text-foreground-inverse bg-button-background-accent hover:bg-button-background-accent-hover',
      },
      size: {
        s: 'btn-label-s py-2 px-4',
        m: 'btn-label py-2.5 px-6',
        l: 'btn-label-l py-3 px-6',
        'icon-m': 'size-10',
        'icon-s': 'size-8',
        'icon-l': 'size-12',
        'icon-xs': 'size-6',
        'icon-tiny': 'size-4',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'm',
    },
  }
);

/**
 * A flexible button component that supports multiple variants and sizes
 * @component
 * @param {Object} props - The component props
 * @param {string} [props.className] - Additional CSS classes to apply to the button
 * @param {ButtonVariant} [props.variant='primary'] - The visual style variant of the button
 * @param {ButtonSize} [props.size='m'] - The size of the button
 * @param {boolean} [props.asChild=false] - When true, allows the button to be rendered as a child component
 * @param {React.ComponentProps<'button'>} props.buttonProps - All standard button HTML attributes
 * @returns {React.ReactElement} A styled button component
 *
 * @example
 * // Primary button with medium size
 * <Button variant="primary" size="m">Click me</Button>
 *
 * // Secondary small button
 * <Button variant="secondary" size="s">Small Button</Button>
 *
 * // Accent large button
 * <Button variant="accent" size="l">Large Button</Button>
 */
function Button({
  className,
  variant,
  size,
  asChild = false,
  'data-track-name': dataTrackName,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> &
  Partial<Record<`data-track-${string}`, string | number | undefined>> & {
    asChild?: boolean;
    'data-track-name'?: string;
    'data-testid'?: string;
  }) {
  const Comp = asChild ? Slot : 'button';
  const dataTestId = props['data-testid'] || 'button';
  const isIconButton = size?.startsWith('icon');
  const ariaLabel = isIconButton ? props['aria-label'] || dataTrackName || 'Button' : props['aria-label'];

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...(dataTrackName && { 'data-track-name': dataTrackName })}
      {...(dataTestId && { 'data-testid': dataTestId })}
      aria-label={ariaLabel}
      {...props}
    />
  );
}

export { Button, type ButtonSize, type ButtonVariant };
